// Pure, UI-agnostic helpers for the Count Set "View Document Lines" action.
//
// A Count Set (OTH-CNS) links several Physical Count documents; the Count Set
// grid rows ARE those documents. "View Document Lines" must show ONLY the
// lines of the SELECTED Physical Count document, replace (never append) the
// displayed collection when switching documents, show an empty state when the
// document has no lines, and never let a slow/failed earlier request overwrite
// a newer selection. This module isolates that logic so it is unit-testable
// without mounting the Vue component (PromotionFormV2/DocumentForm pattern).

// Resolve the selected Physical Count document id from either an explicitly
// passed row or the currently selected grid rows. Returns null when nothing is
// selected. Vue-2 safe (no optional chaining).
function resolveSelectedPcountDocumentId(selectedDocument, selectedDocumentLines) {
  if (selectedDocument != null && selectedDocument.id != null) {
    return selectedDocument.id;
  }
  if (selectedDocumentLines != null && selectedDocumentLines.length > 0 && selectedDocumentLines[0].id != null) {
    return selectedDocumentLines[0].id;
  }
  return null;
}

// Classify the current selection for the "View Document Lines" action. The
// action shows exactly ONE Physical Count document's lines, so an ambiguous
// multi-selection must be rejected (Option A) rather than silently picking the
// first document. An explicitly passed row always wins (per-document trigger).
// Returns one of:
//   { status: 'none' }               -> nothing selected
//   { status: 'multiple' }           -> >1 grid rows selected (ambiguous)
//   { status: 'ok', id: <docId> }    -> a single resolved document id
// Vue-2 safe (no optional chaining).
function classifyViewDocumentLinesSelection(selectedDocument, selectedDocumentLines) {
  if (selectedDocument != null && selectedDocument.id != null) {
    return { status: 'ok', id: selectedDocument.id };
  }
  var count = (selectedDocumentLines != null && selectedDocumentLines.length != null)
    ? selectedDocumentLines.length
    : 0;
  if (count === 0) {
    return { status: 'none' };
  }
  if (count > 1) {
    return { status: 'multiple' };
  }
  if (selectedDocumentLines[0].id != null) {
    return { status: 'ok', id: selectedDocumentLines[0].id };
  }
  return { status: 'none' };
}

// Build the scoped request URL: lines for ONE Physical Count document within
// the Count Set (filtered at the data source by pcount_document_id).
function buildCountSetLinesUrl(endPoint, countSetId, pcountDocumentId) {
  return endPoint + 'documents/get_count_set_pcount_lines?id=' + countSetId
    + '&pcount_document_id=' + pcountDocumentId;
}

// Decide what the displayed collection becomes for a completed request.
// - If the request is not the latest (token mismatch) -> keep current data
//   (ignore the out-of-order / stale response).
// - On success -> REPLACE with the response rows (coerced to an array; empty
//   array when the document has no lines).
// - On failure -> REPLACE with an empty array (clear stale lines).
// Returns { apply, data } where apply=false means "ignore this response".
function resolveCountSetLinesResponse(requestToken, latestToken, response, didError) {
  if (requestToken !== latestToken) {
    return { apply: false, data: null };
  }
  if (didError) {
    return { apply: true, data: [] };
  }
  return { apply: true, data: Array.isArray(response) ? response : [] };
}

export {
  resolveSelectedPcountDocumentId,
  classifyViewDocumentLinesSelection,
  buildCountSetLinesUrl,
  resolveCountSetLinesResponse,
};
